import React, { useState, useEffect } from 'react';
import { useTable, usePagination, useGlobalFilter, useSortBy, useExpanded } from 'react-table';
import 'bootstrap/dist/css/bootstrap.min.css';

function GlobalFilter({ globalFilter, setGlobalFilter, handleDateFilter, onFilter }) {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const applyDateFilter = () => {
    handleDateFilter(fromDate, toDate);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setGlobalFilter(value);
    if (onFilter) {
      onFilter(value);
    }
  };

  const clearSearch = () => {
    setGlobalFilter('');
    if (onFilter) {
      onFilter('');
    }
  };

  return (
    <div className="dataTable_search mb-3">
      <div className="row g-2 align-items-center">
        <div className="col-12 col-md-auto">
          <div className="position-relative" style={{ maxWidth: '250px' }}>
            <input
              value={globalFilter || ''}
              onChange={handleSearchChange}
              className="form-control form-control-sm pe-4"
              placeholder="Search all data..."
            />
            {globalFilter && (
              <span 
                className="position-absolute end-0 top-50 translate-middle-y me-2"
                style={{ cursor: 'pointer', fontSize: '16px', lineHeight: '1' }}
                onClick={clearSearch}
              >
                ×
              </span>
            )}
          </div>
        </div>
        <div className="col-12 col-md-auto">
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="form-control form-control-sm"
              style={{ maxWidth: '130px' }}
            />
            <span className="text-muted">to</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="form-control form-control-sm"
              style={{ maxWidth: '130px' }}
            />
            <button onClick={applyDateFilter} className="btn btn-primary btn-sm">
              Filter
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DataTable({ columns, data, renderRowSubComponent, globalFilter, setGlobalFilter, onFilter }) {
  const [filteredData, setFilteredData] = useState(data);
  const [originalData, setOriginalData] = useState(data);

  useEffect(() => {
    setFilteredData(data);
    setOriginalData(data);
  }, [data]);

  const handleDateFilter = (fromDate, toDate) => {
    if (fromDate || toDate) {
      const filtered = originalData.filter((item) => {
        const itemDates = item.invoices.map(inv => new Date(inv.date).setHours(0, 0, 0, 0));
        const from = fromDate ? new Date(fromDate).setHours(0, 0, 0, 0) : null;
        const to = toDate ? new Date(toDate).setHours(0, 0, 0, 0) : null;

        return itemDates.some(date => 
          (!from || date >= from) && (!to || date <= to)
        );
      });
      setFilteredData(filtered);
    } else {
      setFilteredData(originalData);
    }
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, expanded },
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: { pageIndex: 0 },
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination
  );

  return (
    <div className="dataTable_wrapper">
      <GlobalFilter 
        globalFilter={globalFilter} 
        setGlobalFilter={setGlobalFilter} 
        handleDateFilter={handleDateFilter}
        onFilter={onFilter}
      />

      <div className="table-responsive" style={{ fontSize: '13px' }}>
        <table {...getTableProps()} className="table table-striped table-hover mb-0">
          <thead className="table-dark">
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className="text-nowrap align-middle"
                    style={{ 
                      minWidth: column.minWidth,
                      width: column.width,
                      maxWidth: column.maxWidth
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <span className="me-1">{column.render('Header')}</span>
                      <span className="d-flex flex-column">
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <span className="small">▼</span>
                          ) : (
                            <span className="small">▲</span>
                          )
                        ) : (
                          <span className="small text-muted">↕</span>
                        )}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {page.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-4 text-muted">
                  No records found
                </td>
              </tr>
            ) : (
              page.map((row) => {
                prepareRow(row);
                return (
                  <React.Fragment key={row.id}>
                    <tr {...row.getRowProps()} className="align-middle">
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          className="text-nowrap"
                          style={{ 
                            minWidth: cell.column.minWidth,
                            width: cell.column.width,
                            maxWidth: cell.column.maxWidth
                          }}
                        >
                          {cell.render('Cell')}
                        </td>
                      ))}
                    </tr>
                    {row.isExpanded && (
                      <tr>
                        <td colSpan={columns.length} className="p-0 border-top-0">
                          {renderRowSubComponent({ row })}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="d-flex flex-column flex-sm-row align-items-center justify-content-between gap-2 mt-3 p-2 bg-light rounded">
        <div className="dataTable_pageInfo small">
          Page{' '}
          <strong>
            {pageIndex + 1} of {pageOptions.length}
          </strong>
        </div>
        
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
          >
            Previous
          </button>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => nextPage()}
            disabled={!canNextPage}
          >
            Next
          </button>
        </div>
        
        <div className="d-flex align-items-center gap-2">
          <span className="small text-nowrap">Show:</span>
          <select
            className="form-select form-select-sm"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            style={{ width: 'auto' }}
          >
            {[5, 10, 20, 50].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}